import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Shield, LogIn } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: loginForm.email, password: loginForm.password });
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        toast.info(data.message || "2FA code required");
        setShow2FA(true);
        setUserEmail(data.email);
        setUserId(data.user_id);
        setLoading(false);
        return;
      }
      
      // No 2FA required - proceed with login
      if (data.access_token && data.role) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        if (data.org_type) localStorage.setItem("org_type", data.org_type);
        if (data.user_id) localStorage.setItem("user_id", data.user_id);
        
        toast.success("Login successful!");
        
        // Role-based redirect
        const redirectMap = {
          "admin": "/admin/dashboard",
          "platform_super_admin": "/admin/dashboard",
          "landlord": "/dashboard",
          "property_manager": "/dashboard",
          "hoa": "/dashboard",
          "tenant": "/dashboard"
        };
        
        setTimeout(() => {
          window.location.replace(redirectMap[data.role] || "/dashboard");
        }, 100);
      } else {
        toast.error("Invalid response from server");
        setLoading(false);
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error("Login failed: " + (err.response?.data?.detail || "Invalid credentials"));
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/2fa", {
        email: userEmail,
        code: twoFactorCode,
        user_id: userId
      });
      
      if (data.access_token && data.role) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        if (data.org_type) localStorage.setItem("org_type", data.org_type);
        if (data.user_id) localStorage.setItem("user_id", data.user_id);
        
        toast.success("2FA verification successful!");
        
        // Role-based redirect
        const redirectMap = {
          "admin": "/admin/dashboard",
          "platform_super_admin": "/admin/dashboard",
          "landlord": "/dashboard",
          "property_manager": "/dashboard",
          "hoa": "/dashboard",
          "tenant": "/dashboard"
        };
        
        setTimeout(() => {
          window.location.replace(redirectMap[data.role] || "/dashboard");
        }, 100);
      } else {
        toast.error("Invalid response from server");
        setLoading(false);
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      toast.error("Verification failed: " + (err.response?.data?.detail || "Invalid code"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-4 mb-2">
            <img 
              src="/estate-flow-logo.png" 
              alt="Estate Flow Logo" 
              className="h-16 w-auto"
            />
            <span className="text-2xl font-bold">Estate Flow</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-emerald-600"><Shield className="h-6 w-6" /><span className="font-medium text-lg">Admin Portal</span></div>
          <CardTitle>Secure Access</CardTitle>
        </CardHeader>
        <CardContent>
          {!show2FA ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label>Admin Email</Label>
                <Input 
                  type="email" 
                  placeholder="scurry@sebasalliance.com" 
                  value={loginForm.email} 
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input 
                  type="password" 
                  placeholder="Enter password" 
                  value={loginForm.password} 
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} 
                  required 
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                {loading ? 'Signing in...' : <><LogIn className="mr-2 h-4 w-4" />Access Portal</>}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 font-medium mb-2">🔐 Two-Factor Authentication Required</p>
                <p className="text-xs text-blue-700">
                  Enter the 6-digit code from your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>6-Digit Authentication Code</Label>
                <Input 
                  type="text" 
                  placeholder="000000" 
                  value={twoFactorCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(value);
                  }}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                  required 
                />
                <p className="text-xs text-gray-500 text-center">
                  Codes refresh every 30 seconds
                </p>
              </div>
              
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading || twoFactorCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                onClick={() => {
                  setShow2FA(false);
                  setTwoFactorCode('');
                  setLoading(false);
                }}
              >
                ← Back to Login
              </Button>
            </form>
          )}
          <div className="mt-4 text-center">
            <Button variant="link" onClick={() => navigate('/')}>Back to Home</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;